/*
 * Promoters Page — Conversion-focused landing for influencers, charities, businesses
 * "Stop begging for money" messaging, commission calculator, real-world examples
 */

import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { motion } from "framer-motion";
import {
  Megaphone, DollarSign, ArrowRight, CheckCircle2, X,
  Users, TrendingUp, Zap, Globe, Heart, Building2,
  Mic, Youtube, Instagram, Radio, Church, Briefcase,
  GraduationCap, Trophy, Rocket, Play, Share2, Link2,
  BarChart3, Wallet, Star, Shield, Sparkles,
} from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import { useState, useMemo } from "react";

const oldVsNew = [
  { old: "Selling merch with 5-10% margins", new: "Automatic pro-rata commissions on every ticket you sell" },
  { old: "Begging for donations and tips", new: "Sharing exciting competitions your audience wants to join" },
  { old: "Running ads that annoy your followers", new: "Promoting games that entertain and engage" },
  { old: "Signing up for affiliate programs", new: "Automatic promoter link when you buy a ticket — no sign-up needed" },
  { old: "Affiliate links with 3-5% conversion", new: "Game links with 20-40% conversion (people love competing)" },
  { old: "Patreon/membership fatigue", new: "Fresh, exciting events that keep audiences coming back" },
];

const realWorldExamples = [
  {
    icon: Youtube,
    title: "YouTube Creator — 500K Subscribers",
    scenario: "A tech reviewer promotes a \"Best App Design\" competition to their audience.",
    numbers: "Shares link in video description. 800 fans enter at $10 each. Promoter earns $1,200 (15% commission).",
    takeaway: "One mention in one video = $1,200. No inventory, no shipping, no customer service.",
    color: "text-neon",
  },
  {
    icon: Instagram,
    title: "Instagram Influencer — 100K Followers",
    scenario: "A fitness influencer promotes a \"30-Day Transformation Challenge\" in their stories.",
    numbers: "300 followers enter at $20 each. Promoter earns $900 (15%). Plus their audience is more engaged than ever.",
    takeaway: "Stories + swipe-up link = $900 in passive income. The audience loves it because they can win prizes.",
    color: "text-gold",
  },
  {
    icon: Heart,
    title: "Nonprofit — Children's Hospital",
    scenario: "A charity promotes a holiday baking competition. All promoter earnings go to the cause.",
    numbers: "1,000 bakers enter at $10 each. $1,500 goes to the charity. Plus $7,000 in prizes keeps participants excited.",
    takeaway: "Fundraising through engagement, not guilt. Donors become competitors. Everyone has fun.",
    color: "text-neon",
  },
  {
    icon: Church,
    title: "Church / Religious Organization",
    scenario: "A church promotes a community talent show competition to raise funds for a building project.",
    numbers: "500 members enter at $5 each. Church earns $375 (15%). Winners get prizes. Community bonds strengthen.",
    takeaway: "Congregation engagement + fundraising in one event. No bake sales, no car washes.",
    color: "text-gold",
  },
  {
    icon: Briefcase,
    title: "Local Business — Restaurant",
    scenario: "A restaurant promotes a \"Best Home Recipe\" competition to drive foot traffic and brand awareness.",
    numbers: "400 local food lovers enter at $10 each. Restaurant earns $600 promoting. Winner gets a $2,000 prize + featured on the menu.",
    takeaway: "Marketing that pays you instead of costing you. Plus massive local engagement and press coverage.",
    color: "text-neon",
  },
  {
    icon: Radio,
    title: "Podcast Host — 50K Listeners",
    scenario: "A true crime podcast promotes a \"Best Short Mystery Story\" writing competition.",
    numbers: "600 listeners enter at $15 each. Promoter earns $1,350. Best stories get read on the podcast.",
    takeaway: "Content creation + revenue generation in one move. Listeners become participants become loyal fans.",
    color: "text-gold",
  },
  {
    icon: Mic,
    title: "Twitch Streamer — 10K Concurrent",
    scenario: "A gaming streamer promotes a viewer tournament in their favorite game.",
    numbers: "1,000 viewers enter at $5 each. Streamer earns $750 promoting. Tournament content drives 3x normal viewership.",
    takeaway: "More viewers, more engagement, more income — all from one tournament link in chat.",
    color: "text-neon",
  },
  {
    icon: GraduationCap,
    title: "University Club",
    scenario: "A student organization promotes an inter-university debate competition.",
    numbers: "200 students enter at $10 each. Club earns $300. Winners get real prizes. Sponsors love the engagement numbers.",
    takeaway: "Student orgs can fund their activities through competitions instead of bake sales and car washes.",
    color: "text-gold",
  },
];

const promoterBenefits = [
  "Automatic promoter link — buy a ticket and you're a promoter instantly",
  "Pro-rata earnings — sell 80% of tickets, earn 80% of the promoter pool",
  "No products to create, ship, or manage",
  "No customer service — GamePoint handles everything",
  "Works for any audience size — 100 followers or 10 million",
  "Competitions are exciting — your audience actually wants to participate",
  "Real-time dashboard showing clicks, entries, and earnings",
  "Global reach — promote to audiences anywhere in the world",
  "Stack with other revenue — this doesn't replace your income, it adds to it",
];

export default function Promoters() {
  const [audienceSize, setAudienceSize] = useState(10000);
  const [conversionRate, setConversionRate] = useState(5);
  const [avgEntryFee, setAvgEntryFee] = useState(10);
  const [commissionPct, setCommissionPct] = useState(15);
  const [gamesPerMonth, setGamesPerMonth] = useState(4);

  const earnings = useMemo(() => {
    const playersPerGame = Math.round(audienceSize * (conversionRate / 100));
    const perGame = playersPerGame * avgEntryFee * (commissionPct / 100);
    const monthly = perGame * gamesPerMonth;
    const yearly = monthly * 12;
    return { playersPerGame, perGame, monthly, yearly };
  }, [audienceSize, conversionRate, avgEntryFee, commissionPct, gamesPerMonth]);

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* Hero */}
      <section className="relative pt-24 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-teal/5 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-neon/10 border border-neon/20 text-neon text-xs font-medium mb-6">
              <Megaphone className="w-3.5 h-3.5" />
              For Promoters
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.08] tracking-tight mb-6">
              Buy a Ticket.{" "}
              <span className="text-gradient-neon">Share Your Link.</span>{" "}
              Get Paid.
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl">
              Every ticket buyer automatically gets a unique promoter link. Share it anywhere —
              social media, email, text, Discord. When someone buys a ticket through your link,
              you earn a pro-rata share of the promoter pool. Sell more tickets = earn more money.
              No sign-up required. No minimum audience. Just share and earn.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/games">
                <Button size="lg" className="bg-neon hover:bg-neon-light text-background font-semibold gap-2 h-12 px-8">
                  <Share2 className="w-5 h-5" />
                  Find Games to Promote
                </Button>
              </Link>
              <Link href="/create">
                <Button size="lg" variant="outline" className="h-12 px-8 border-border/50 hover:border-neon/50 hover:text-neon gap-2">
                  Or Create & Promote Your Own
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Old Way vs GamePoint Way */}
      <section className="py-24 px-6 bg-secondary/20">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            The <span className="text-gradient-neon">Old Way</span> vs The <span className="text-gradient-gold">GamePoint Way</span>
          </h2>
          <p className="text-muted-foreground text-center max-w-xl mx-auto mb-12">
            Traditional audience monetization is broken. Here's how GamePoint fixes it.
          </p>

          <div className="space-y-3">
            {oldVsNew.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="grid md:grid-cols-2 gap-3"
              >
                <div className="glass-panel rounded-xl p-4 flex items-center gap-3 border-destructive/20">
                  <X className="w-5 h-5 text-destructive shrink-0" />
                  <p className="text-sm text-muted-foreground">{item.old}</p>
                </div>
                <div className="glass-panel rounded-xl p-4 flex items-center gap-3 border-neon/20">
                  <CheckCircle2 className="w-5 h-5 text-neon shrink-0" />
                  <p className="text-sm text-foreground/90">{item.new}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Calculator */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            <span className="text-gradient-neon">Commission</span> Calculator
          </h2>
          <p className="text-muted-foreground text-center max-w-xl mx-auto mb-12">
            See what you could earn by promoting games to your audience. Adjust the sliders.
          </p>

          <div className="glass-panel rounded-2xl p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 flex justify-between">
                    <span>Your Audience Size</span>
                    <span className="text-neon font-mono">{audienceSize.toLocaleString()}</span>
                  </label>
                  <input type="range" min={100} max={1000000} step={100} value={audienceSize} onChange={(e) => setAudienceSize(Number(e.target.value))} className="w-full accent-teal" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 flex justify-between">
                    <span>Conversion Rate</span>
                    <span className="text-neon font-mono">{conversionRate}%</span>
                  </label>
                  <input type="range" min={1} max={20} value={conversionRate} onChange={(e) => setConversionRate(Number(e.target.value))} className="w-full accent-teal" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 flex justify-between">
                    <span>Average Entry Fee</span>
                    <span className="text-neon font-mono">${avgEntryFee}</span>
                  </label>
                  <input type="range" min={1} max={100} value={avgEntryFee} onChange={(e) => setAvgEntryFee(Number(e.target.value))} className="w-full accent-teal" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 flex justify-between">
                    <span>Your Commission</span>
                    <span className="text-neon font-mono">{commissionPct}%</span>
                  </label>
                  <input type="range" min={5} max={15} value={commissionPct} onChange={(e) => setCommissionPct(Number(e.target.value))} className="w-full accent-teal" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 flex justify-between">
                    <span>Games Promoted Per Month</span>
                    <span className="text-neon font-mono">{gamesPerMonth}</span>
                  </label>
                  <input type="range" min={1} max={20} value={gamesPerMonth} onChange={(e) => setGamesPerMonth(Number(e.target.value))} className="w-full accent-teal" />
                </div>
              </div>

              <div className="flex flex-col justify-center space-y-4">
                <div className="glass-panel rounded-xl p-4">
                  <p className="text-sm text-muted-foreground">Players Per Game (from your link)</p>
                  <p className="text-2xl font-bold font-mono text-neon">{earnings.playersPerGame.toLocaleString()}</p>
                </div>
                <div className="glass-panel rounded-xl p-4">
                  <p className="text-sm text-muted-foreground">Earnings Per Game</p>
                  <p className="text-2xl font-bold font-mono text-neon">${earnings.perGame.toLocaleString()}</p>
                </div>
                <div className="glass-panel rounded-xl p-4 border border-neon/20">
                  <p className="text-sm text-muted-foreground">Monthly Earnings</p>
                  <p className="text-3xl font-bold font-mono text-gradient-neon">${earnings.monthly.toLocaleString()}</p>
                </div>
                <div className="glass-panel rounded-xl p-4 border border-gold/20">
                  <p className="text-sm text-muted-foreground">Yearly Earnings</p>
                  <p className="text-3xl font-bold font-mono text-gradient-gold">${earnings.yearly.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Promoters Earn */}
      <section className="py-24 px-6 bg-secondary/20">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            How <span className="text-gradient-neon">Promoters</span> Earn
          </h2>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "1", icon: Play, title: "Buy a Ticket", desc: "Enter any game that interests you. The moment you buy a ticket, you automatically receive a unique promoter link for that game." },
              { step: "2", icon: Share2, title: "Share Your Link", desc: "Post it on social media, email it to your list, mention it on your podcast, drop it in your Discord. Anywhere your audience is." },
              { step: "3", icon: Users, title: "Others Buy Through You", desc: "When someone buys a ticket through your link, your share of the promoter pool grows. It's tracked automatically — pro-rata based on tickets sold." },
              { step: "4", icon: Wallet, title: "Earn Pro-Rata", desc: "Sell 80 of 100 tickets? You earn 80% of the promoter pool. The more you sell, the bigger your share. Cash out anytime." },
            ].map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-neon/10 border border-neon/20 flex items-center justify-center mx-auto mb-4">
                  <s.icon className="w-6 h-6 text-neon" />
                </div>
                <h3 className="text-base font-bold mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Real-World Examples */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Who Can Be a <span className="text-gradient-gold">Promoter</span>?
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            Anyone with an audience. Influencers, businesses, charities, churches, schools,
            podcasters, streamers — if people listen to you, you can earn with GamePoint.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {realWorldExamples.map((ex, i) => (
              <motion.div
                key={ex.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="glass-panel rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg ${ex.color === "text-gold" ? "bg-gold/10" : "bg-neon/10"} flex items-center justify-center`}>
                    <ex.icon className={`w-5 h-5 ${ex.color}`} />
                  </div>
                  <h3 className="font-semibold">{ex.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{ex.scenario}</p>
                <p className="text-sm font-mono text-foreground/80 mb-2 bg-background/50 rounded-lg px-3 py-2">{ex.numbers}</p>
                <p className="text-sm text-neon font-medium">{ex.takeaway}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 px-6 bg-secondary/20">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why <span className="text-gradient-neon">Promote</span> on GamePoint?
              </h2>
              <ul className="space-y-3">
                {promoterBenefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-neon shrink-0 mt-0.5" />
                    <span className="text-foreground/80">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              <div className="glass-panel rounded-xl p-6 border border-neon/20">
                <div className="flex items-center gap-3 mb-3">
                  <BarChart3 className="w-5 h-5 text-neon" />
                  <h3 className="font-semibold">Real-Time Analytics</h3>
                </div>
                <p className="text-sm text-muted-foreground">Track every click, every entry, every dollar earned. Your promoter dashboard shows exactly how your audience is engaging.</p>
              </div>
              <div className="glass-panel rounded-xl p-6 border border-gold/20">
                <div className="flex items-center gap-3 mb-3">
                  <Sparkles className="w-5 h-5 text-gold" />
                  <h3 className="font-semibold">High Conversion Rates</h3>
                </div>
                <p className="text-sm text-muted-foreground">Competitions are inherently exciting. People want to compete. Average conversion rates are 5-15x higher than typical affiliate links.</p>
              </div>
              <div className="glass-panel rounded-xl p-6 border border-neon/20">
                <div className="flex items-center gap-3 mb-3">
                  <Globe className="w-5 h-5 text-neon" />
                  <h3 className="font-semibold">Works for Any Cause</h3>
                </div>
                <p className="text-sm text-muted-foreground">Growing a YouTube channel? Raising money for charity? Building a local business? Funding a school club? GamePoint works for all of it.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center glass-panel rounded-2xl p-12 glow-neon">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Your Audience Is <span className="text-gradient-neon">Ready to Compete</span>
          </h2>
          <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
            Stop leaving money on the table. Every time your audience scrolls past your content,
            they could be entering a competition — and you could be earning a commission.
            Start promoting today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/games">
              <Button size="lg" className="bg-neon hover:bg-neon-light text-background font-semibold gap-2 h-12 px-8">
                <Share2 className="w-5 h-5" />
                Find Games to Promote
              </Button>
            </Link>
            <Link href="/create">
              <Button size="lg" variant="outline" className="h-12 px-8 border-neon/30 hover:border-neon text-neon gap-2">
                <Rocket className="w-5 h-5" />
                Create & Promote Your Own
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
